import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, InlineStack, TextField, DropZone, Text, Button, Icon, Spinner, ButtonGroup } from '@shopify/polaris';
import { MagicMinor, UploadMajor, ImportMinor, StarFilledMinor, ChevronLeftMinor, ChevronRightMinor } from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const AIDesignUpscalePage = forwardRef(function AIDesignUpscalePage(
  {
    embedded = false,
    storyName = '',
    sceneName = '',
    initialOutputImageUrl = '',
    setToastMessage,
    setToastActive,
  },
  ref
) {
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [recentDesigns, setRecentDesigns] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [recentStartIndex, setRecentStartIndex] = useState(0);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [azureSettings, setAzureSettings] = useState(null);
  const [uploadedFileAzureUrl, setUploadedFileAzureUrl] = useState('');
  const [uploadedFileKey, setUploadedFileKey] = useState('');
  const hasInitializedFromPropsRef = useRef(false);
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [isFavouriting, setIsFavouriting] = useState(false);

  const [model, setModel] = useState('reality'); // 'anime' | 'reality'
  const [upscaleFactor, setUpscaleFactor] = useState(4); // 1 | 2 | 4

  // Before/After slider state for upscale preview
  const previewContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderRatio, setSliderRatio] = useState(0.5); // 0..1

  const handleDownloadPreview = useCallback(async () => {
    if (!selectedPreviewUrl) return;
    try {
      const resp = await fetch(selectedPreviewUrl, { mode: 'cors' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (selectedPreviewUrl.split('/').pop() || 'image').split('?')[0] || 'image';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_ignore) {}
  }, [selectedPreviewUrl]);

  const handleDrop = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setUploadedFiles(files);
    const first = files?.[0];
    const newKey = first ? `${first.name}|${first.size}|${first.lastModified}` : '';
    if (newKey !== uploadedFileKey) {
      setUploadedFileAzureUrl('');
      setUploadedFileKey(newKey);
    }
  }, [uploadedFileKey]);

  const firstPreviewUrl = uploadedFiles?.[0]
    ? URL.createObjectURL(uploadedFiles[0])
    : undefined;

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      if (imageUrl) setImageUrl('');
      if (selectedPreviewUrl) setSelectedPreviewUrl('');
    }
  }, [uploadedFiles.length]);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setIsLoadingRecent(true);
        const baseUrl = getServerURL();
        const params = new URLSearchParams({ limit: '10' });
        params.set('type', 'upscale');
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const resp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (!resp.ok) return;
        const json = await resp.json();
        const items = Array.isArray(json?.data) ? json.data : [];
        setRecentDesigns(items);
      } catch (_err) {
        setRecentDesigns([]);
      } finally {
        setIsLoadingRecent(false);
      }
    };
    fetchRecent();
  }, [storyName, sceneName]);

  const loadAzureSettings = useCallback(async () => {
    try {
      const baseUrl = getServerURL();
      const resp = await fetch(`${baseUrl}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json?.success && json?.data && json.data.AZURE_BLOB_BASE_URL && json.data.AZURE_SAS_TOKEN) {
        setAzureSettings(json.data);
        return json.data;
      }
      return null;
    } catch (_e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (hasInitializedFromPropsRef.current) return;
    const outputUrl = initialOutputImageUrl || '';
    if (outputUrl) {
      setImageUrl(outputUrl);
      setSelectedPreviewUrl(outputUrl);
    }
    hasInitializedFromPropsRef.current = true;
  }, []);

  useEffect(() => {
    loadAzureSettings();
  }, [loadAzureSettings]);

  const uploadFileToAzure = useCallback(async (file) => {
    if (!azureSettings) throw new Error('Azure settings not loaded');
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
    const randomTxt = `${Math.random().toString(36).substring(2, 8)}${Date.now()}`;
    const newFileName = `design_input_${randomTxt}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(newFileName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type || 'image/png' },
    });
    return publicUrl;
  }, [azureSettings]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    let finalImageUrl = (imageUrl || '').trim();
    try {
      setIsGenerating(true);
      if (!finalImageUrl) {
        if (uploadedFiles.length === 0) {
          setIsGenerating(false);
          return;
        }
        const first = uploadedFiles[0];
        const currentKey = `${first.name}|${first.size}|${first.lastModified}`;
        if (uploadedFileAzureUrl && currentKey === uploadedFileKey) {
          finalImageUrl = uploadedFileAzureUrl;
        } else {
          if (!azureSettings) {
            const settings = await loadAzureSettings();
            if (!settings) {
              throw new Error('Azure settings not available');
            }
          }
          finalImageUrl = await uploadFileToAzure(first);
          setUploadedFileAzureUrl(finalImageUrl);
          setUploadedFileKey(currentKey);
        }
      }

      const resp = await fetch(`${baseUrl}/design/upscale`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: finalImageUrl,
          upscale_factor: upscaleFactor,
          model,
          story: storyName || '',
          scene: sceneName || '',
        }),
      });

      let apiOutputUrl = '';
      let apiDesignId = '';
      try {
        const data = await resp.clone().json();
        const candidates = [data?.data, data?.result, data?.design, data?.data?.design, data];
        for (const c of candidates) {
          if (!c || typeof c !== 'object') continue;
          if (!apiOutputUrl) {
            apiOutputUrl = c.imageUrl || c.outputImageUrl || '';
          }
          if (!apiDesignId) {
            apiDesignId = c._id || c.id || c.designId || c.design_id || '';
          }
          if (apiOutputUrl && apiDesignId) break;
        }
      } catch (_ignore) {}

      setImageUrl(finalImageUrl);
      setSelectedPreviewUrl(apiOutputUrl || finalImageUrl);
      setSliderRatio(0.5);
      if (apiDesignId) setSelectedDesignId(apiDesignId);

      try {
        const params = new URLSearchParams({ limit: '10' });
        params.set('type', 'upscale');
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const recentResp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (recentResp.ok) {
          const recentJson = await recentResp.json();
          const items = Array.isArray(recentJson?.data) ? recentJson.data : [];
          setRecentDesigns(items);
          if (!apiDesignId && (apiOutputUrl || finalImageUrl)) {
            const match = items.find((it) =>
              it?.outputImageUrl === apiOutputUrl ||
              it?.imageUrl === apiOutputUrl ||
              it?.outputImageUrl === selectedPreviewUrl ||
              it?.inputImageUrl === finalImageUrl ||
              it?.imageUrl === finalImageUrl
            );
            if (match) {
              const inferredId = match._id || match.id || match.designId || match.design_id || '';
              if (inferredId) {
                setSelectedDesignId(inferredId);
              }
            }
          }
        }
      } catch (_e) {}
    } catch (_err) {
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, imageUrl, uploadedFiles, model, upscaleFactor, uploadFileToAzure]);

  const handleAddToFavourites = useCallback(async () => {
    if (!selectedDesignId || isFavouriting) return;
    try {
      setIsFavouriting(true);
      const baseUrl = getServerURL();
      await fetch(`${baseUrl}/design/favorites`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ design_id: selectedDesignId }),
      });
      if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
        setToastMessage('Added to favourites');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 2000);
      }
    } catch (_err) {
    } finally {
      setIsFavouriting(false);
    }
  }, [selectedDesignId, isFavouriting]);

  useImperativeHandle(ref, () => ({
    generate: handleGenerate,
    getPreviewUrl: () => selectedPreviewUrl || imageUrl || '',
  }), [handleGenerate, selectedPreviewUrl, imageUrl]);

  const handleRecentClick = useCallback((item) => {
    if (!item) return;
    const inputUrl = item.inputImageUrl || item.imageUrl || '';
    const outputUrl = item.outputImageUrl || inputUrl || '';
    setUploadedFiles([]);
    setImageUrl(inputUrl);
    setSelectedPreviewUrl(outputUrl);
    setSliderRatio(0.5);
    if (item?._id) setSelectedDesignId(item._id);
  }, []);

  // Slider interaction handlers
  const updateSliderFromEvent = useCallback((event) => {
    const container = previewContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clientX = (event.touches && event.touches[0]?.clientX) || event.clientX;
    if (typeof clientX !== 'number') return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setSliderRatio(ratio);
  }, []);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    updateSliderFromEvent(e);
  }, [updateSliderFromEvent]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    updateSliderFromEvent(e);
  }, [isDragging, updateSliderFromEvent]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    updateSliderFromEvent(e);
  }, [updateSliderFromEvent]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    updateSliderFromEvent(e);
  }, [isDragging, updateSliderFromEvent]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>
        {`
        .NoMarginButtonRow .Polaris-Text--root.Polaris-Text--bodySm.Polaris-Text--medium {
          display: flex;
          align-items: center;
        }
        `}
      </style>
      <InlineStack gap="400" wrap={false}>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '33.3333%', maxHeight: embedded ? '70vh' : '100vh', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12 }}>
            <Text variant="headingMd" as="h2">Reference Image</Text>
          </div>
          <TextField
            label="Image URL"
            value={imageUrl}
            onChange={(value) => {
              if (value && uploadedFiles.length > 0) {
                setUploadedFiles([]);
              }
              setImageUrl(value);
            }}
            placeholder="https://example.com/image.png"
            autoComplete="off"
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 8px' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <Text as="span" tone="subdued">-OR-</Text>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          <DropZone accept="image/*" allowMultiple={false} onDrop={handleDrop} variableHeight>
            <div style={{ padding: 16, textAlign: 'center', width: '100%' }}>
              {firstPreviewUrl ? (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button
                    onClick={() => setUploadedFiles([])}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 18,
                      fontWeight: 'bold',
                      zIndex: 2,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                    }}
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    ×
                  </button>
                  <img
                    src={firstPreviewUrl}
                    alt="Uploaded preview"
                    style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <Icon source={UploadMajor} tone="base" />
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Drag and drop an image here, or click to upload
                  </Text>
                </div>
              )}
            </div>
          </DropZone>

          <div className="NoMarginButtonRow" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <ButtonGroup segmented>
              <Button
                pressed={model === 'anime'}
                onClick={() => setModel('anime')}
                style={{ margin: 0, minWidth: 140 }}
              >
                Anime
              </Button>
              <Button
                pressed={model === 'reality'}
                onClick={() => setModel('reality')}
                style={{ margin: 0, minWidth: 140 }}
              >
                Reality
              </Button>
            </ButtonGroup>
          </div>

          <div className="NoMarginButtonRow" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <ButtonGroup segmented>
              <Button
                pressed={upscaleFactor === 1}
                onClick={() => setUpscaleFactor(1)}
                style={{ margin: 0, minWidth: 140 }}
              >
                1x
              </Button>
              <Button
                pressed={upscaleFactor === 2}
                onClick={() => setUpscaleFactor(2)}
                style={{ margin: 0, minWidth: 140 }}
              >
                2x
              </Button>
              <Button
                pressed={upscaleFactor === 4}
                onClick={() => setUpscaleFactor(4)}
                style={{ margin: 0, minWidth: 140 }}
              >
                4x
              </Button>
            </ButtonGroup>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={MagicMinor} variant="primary" loading={isGenerating} onClick={handleGenerate} style={{ minWidth: 140 }}>
              Upscale
            </Button>
          </div>
        </Box>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '66.6667%', maxHeight: embedded ? '70vh' : '100vh', overflow: 'hidden' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="headingMd" as="h2">Preview</Text>
            <InlineStack gap="200" align="center">
              <Button icon={StarFilledMinor} onClick={handleAddToFavourites} disabled={!selectedDesignId || isFavouriting}>
                Add to Favourite
              </Button>
              <Button icon={ImportMinor} onClick={handleDownloadPreview} disabled={!selectedPreviewUrl}>
                Download
              </Button>
            </InlineStack>
          </div>
          <div
            style={{
              width: '100%',
              height: 'calc(100% - 40px)',
              background: '#f5f6f7',
              borderRadius: 12,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            ref={previewContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {selectedPreviewUrl ? (
              (imageUrl && selectedPreviewUrl) ? (
                <div style={{ position: 'absolute', inset: 0 }}>
                  {/* Base: input (before) */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                  {/* Overlay: output (after), clipped by clip-path to keep same scaling */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${selectedPreviewUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      WebkitClipPath: `polygon(0 0, ${Math.round(sliderRatio * 100)}% 0, ${Math.round(sliderRatio * 100)}% 100%, 0 100%)`,
                      clipPath: `polygon(0 0, ${Math.round(sliderRatio * 100)}% 0, ${Math.round(sliderRatio * 100)}% 100%, 0 100%)`,
                    }}
                  />
                  {/* Labels */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      padding: '4px 8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      zIndex: 2,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    After
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      padding: '4px 8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      zIndex: 2,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    Before
                  </div>
                  {/* Divider handle */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${Math.round(sliderRatio * 100)}%`,
                      transform: 'translateX(-50%)',
                      width: 2,
                      background: 'rgba(0,0,0,0.35)',
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
                      cursor: 'ew-resize',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'white',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${selectedPreviewUrl})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              )
            ) : (
              <Text tone="subdued">No image selected</Text>
            )}
            {isGenerating && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size="large" />
              </div>
            )}
          </div>
        </Box>
      </InlineStack>
      <Box
        padding="400"
        borderWidth="0"
        borderColor="transparent"
        borderRadius="0"
        minHeight="1024px"
        className="Polaris-ShadowBevel"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text variant="headingMd" as="h2">Recent Generation</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={ChevronLeftMinor}
              onClick={() => setRecentStartIndex((idx) => Math.max(0, idx - 10))}
              disabled={recentStartIndex === 0}
            />
            <Button
              icon={ChevronRightMinor}
              onClick={() => setRecentStartIndex((idx) => Math.min(Math.max(0, recentDesigns.length - 10), idx + 10))}
              disabled={recentDesigns.length <= 10 || recentStartIndex >= Math.max(0, recentDesigns.length - 10)}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 16 }}>
          {(() => {
            const tiles = new Array(10).fill(null).map((_, i) => recentDesigns[recentStartIndex + i] || null);
            return tiles.map((item, idx) => (
              <div
                key={item?._id || `empty-${idx}`}
                className="Polaris-ShadowBevel"
                style={{
                  padding: 0,
                  width: '100%',
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  background: '#f5f6f7',
                  display: 'block',
                  cursor: item ? 'pointer' : 'default',
                }}
                role={item ? 'button' : undefined}
                tabIndex={item ? 0 : undefined}
                onClick={item ? () => handleRecentClick(item) : undefined}
                onKeyDown={item ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRecentClick(item); } } : undefined}
              >
                {item ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${item.outputImageUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                ) : null}
              </div>
            ));
          })()}
        </div>
      </Box>
    </div>
  );
});

export default AIDesignUpscalePage;


